/**
 * Recovers from missing build files after a deployment.
 *
 * Every page is lazy loaded and its file name contains a content hash. When a new release is deployed, a tab that
 * was opened before the deployment still references the old hashes, which no longer exist on the server, so opening
 * another page fails with "Failed to fetch dynamically imported module". Reloading the page fetches the new build.
 *
 * A short guard stored in the session prevents an endless reload loop if the files are really missing.
 */

const reloadGuardStorageKey = 'stale-build-recovery:reloaded-at';
const reloadGuardMilliseconds = 10e3;

const chunkLoadErrorPatterns = [
    /Failed to fetch dynamically imported module/i, // Chromium
    /error loading dynamically imported module/i, // Firefox
    /Importing a module script failed/i, // Safari
    /Unable to preload CSS/i, // Vite CSS preload
    /is not a valid JavaScript MIME type/i, // The missing file was answered with the HTML page
];

function isChunkLoadError(error) {
    const message = String(error?.message ?? error ?? '');

    return chunkLoadErrorPatterns.some((pattern) => pattern.test(message));
}

function readLastReloadTime() {
    try {
        return Number(window.sessionStorage.getItem(reloadGuardStorageKey)) || 0;
    } catch {
        return 0;
    }
}

function writeLastReloadTime(time) {
    try {
        window.sessionStorage.setItem(reloadGuardStorageKey, String(time));
    } catch {
        // Storage is unavailable (e.g. private mode), the reload still happens without the guard
    }
}

export default class StaleBuildRecovery {
    constructor(router) {
        this.router = router;
        this.reloadScheduled = false;
    }

    /**
     * @returns {boolean} Whether a reload was started
     */
    reload(targetUrl = null) {
        if (this.reloadScheduled) {
            return true;
        }

        const now = Date.now();

        if (now - readLastReloadTime() < reloadGuardMilliseconds) {
            console.error('Build files are still missing after a reload, giving up to avoid a reload loop.');
            return false;
        }

        this.reloadScheduled = true;
        writeLastReloadTime(now);

        if (targetUrl) {
            window.location.assign(targetUrl);
        } else {
            window.location.reload();
        }

        return true;
    }

    install() {
        // Navigation failures carry the target route, so the user lands where they wanted to go
        this.router.onError((error, to) => {
            if (isChunkLoadError(error)) {
                this.reload(to?.fullPath ?? null);
            }
        });

        // Fallback for dynamic imports outside the router. Deferred, so the router handler above (which knows the
        // target route) runs first when both are triggered by the same failure.
        window.addEventListener('vite:preloadError', () => {
            setTimeout(() => this.reload(), 0);
        });
    }
}
